import React, { useState, useMemo, useCallback, useContext, createContext } from "react"
import createPersistedState from "use-persisted-state"
import produce from "immer"
import { useProducts } from "frontend/src/utils/useProducts"

const useCartState = createPersistedState("sleepink-cart")
const useCouponState = createPersistedState("sleepink-coupon")

const newID = getUUID()

const exampleInput = {
  productID: "",
  variantID: "",
}

const exampleEntry = {
  ...exampleInput,
  subscription: false,
  subscriptionID: "",
  quantity: 1,
  key: newID,
  price: 0,
}
const exampleLineItems = [exampleEntry]
// eslint-disable-next-line no-unused-vars
const exampleItems = { [newID]: exampleEntry }

const CartContext = createContext({
  lineItems: exampleLineItems,
  numberOfItems: 0,
  expanded: false,
  toggle: () => {},
  show: () => {},
  hide: () => {},
  addSingle: (toAdd = exampleInput) => {},
  addMultiple: (toAdd = exampleInput, quantity = 1) => {},
  decrementQuantity: (key = "") => {},
  incrementQuantity: (key = "") => {},
  changeQuantity: (key = "", newQuantity = 0) => {},
  changeSubscription: (key = "", subscriptionID = "") => {},
  changeVariant: (key = "", variantID = "") => {},
  remove: (key = "") => {},
  prices: { preDiscountTotal: 0, shipping: 0, saving: 0, finalPrice: 0 },
  checkoutURL: "",
  coupon: null,
  couponError: "",
  tryCoupon: coupon => Promise.resolve(true),
})

function useCart() {
  return useContext(CartContext)
}

function buildCheckoutURL({ lineItems, products, coupon }) {
  const canShopify = lineItems.reduce((prev, { subscription }) => prev && !subscription, true)
  const shopifyProductList = lineItems
    .map(
      ({ productID, variantID, quantity }) =>
        products[productID].variants.find(({ ourID }) => ourID === variantID).shopifyID +
        ":" +
        quantity
    )
    .join(",")
  // TODO we need to add some custom code to woocommerce to accept multiple add to cart items, see
  // https://stackoverflow.com/questions/42570982/adding-multiple-items-to-woocommerce-cart-at-once
  // https://stackoverflow.com/questions/37610345/woocommerce-how-to-add-multiple-products-to-cart-at-once
  const woocommerceProductList = lineItems
    .map(({ productID, variantID, quantity, subscription, subscriptionID }) => {
      const variant = products[productID].variants.find(({ ourID }) => ourID === variantID)
      const subscriptionData = subscription
        ? variant.subscriptions.find(({ ourID }) => ourID === subscriptionID)
        : null
      const selectedWooID = subscriptionData
        ? subscriptionData.woocommerceID
        : variant.woocommerceID
      return `${selectedWooID}:${quantity}`
    })
    .join(",")
  if (canShopify) {
    return `https://sleep-ink.myshopify.com/cart/${shopifyProductList}?${
      coupon ? "discount=" + coupon.couponName : ""
    }`
  } else {
    return `https://abo.schlafdrink.de/cart/${woocommerceProductList}?${
      coupon ? "&discount=" + coupon.couponName : ""
    }`
  }
}

function calculateSaving(coupon, amount) {
  if (!coupon) {
    return 0
  }
  if (amount < (coupon.couponMinAmount || 0)) {
    return 0
  } else if (coupon.couponType === "percent") {
    return Math.round(amount * (0.01 * coupon.couponAmount))
  } else if (coupon.couponType === "fixed_cart") {
    return coupon.couponAmount
  }
}

const baseShipping = 380
const minForFreeShipping = 2900

function calculatePrices({ lineItems, products, coupon }) {
  let preDiscountTotal = 0
  // @ts-ignore
  // eslint-disable-next-line no-unused-vars
  let subscriptionDiscountSum = 0
  lineItems.forEach(line => {
    const variant = products[line.productID].variants.find(({ ourID }) => ourID === line.variantID)
    const subscription = variant.subscriptions.find(({ ourID }) => ourID === line.subscriptionID)
    if (subscription && line.subscription) {
      preDiscountTotal += subscription.price * line.quantity
      subscriptionDiscountSum += (variant.price - subscription.price) * line.quantity
    } else {
      preDiscountTotal += variant.price * line.quantity
    }
  })

  const saving = calculateSaving(coupon, preDiscountTotal)
  const postDiscountTotal = preDiscountTotal - saving
  const positiveDiscountTotal = postDiscountTotal <= 0 ? 0 : postDiscountTotal
  const shipping = positiveDiscountTotal >= minForFreeShipping ? 0 : baseShipping
  const finalPrice = postDiscountTotal >= 0 ? preDiscountTotal - saving + shipping : shipping
  return { preDiscountTotal, shipping, saving, finalPrice }
}

function calculatePriceForLineItem(item, products) {
  const product = products[item.productID]
  const variant = product.variants.find(({ ourID }) => item.variantID === ourID)
  const piecePrice = item.subscription
    ? variant.subscriptions.find(({ ourID }) => ourID === item.subscriptionID).price
    : variant.price
  return piecePrice * item.quantity
}

function getUUID() {
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, function(c) {
    var r = (Math.random() * 16) | 0,
      v = c === "x" ? r : (r & 0x3) | 0x8
    return v.toString(16)
  })
}

function CartProvider({ children }) {
  /** @type {[typeof exampleItems, (items: any) => void]} */
  const [items, setItems] = useCartState({})
  const [expanded, setExpanded] = useState(false)
  const products = useProducts()
  const [coupon, setCoupon] = useCouponState(null)
  const [couponError, setCouponError] = useState("")

  const lineItems = useMemo(
    () =>
      Object.values(items).map(itemData => ({
        ...itemData,
        price: calculatePriceForLineItem(itemData, products),
      })),
    [items, products]
  )
  const numberOfItems = useMemo(
    () => Object.values(items).reduce((prevSum, { quantity }) => prevSum + quantity, 0),
    [items]
  )

  const prices = useMemo(() => calculatePrices({ lineItems, products, coupon }), [
    coupon,
    lineItems,
    products,
  ])
  const checkoutURL = useMemo(() => buildCheckoutURL({ lineItems, products, coupon }), [
    coupon,
    lineItems,
    products,
  ])

  const toggle = useCallback(() => setExpanded(e => !e), [])
  const show = useCallback(() => setExpanded(true), [])
  const hide = useCallback(() => setExpanded(false), [])
  const addMultiple = useCallback(
    (toAdd, quantity) => {
      return setItems(i =>
        produce(i, draft => {
          const inCart = Object.values(i).find(
            ({ variantID, subscription }) => variantID === toAdd.variantID && !subscription
          )
          if (inCart) {
            draft[inCart.key].quantity += quantity
          } else {
            const newKey = getUUID()
            draft[newKey] = {
              productID: toAdd.productID,
              variantID: toAdd.variantID,
              subscription: false,
              subscriptionID: "",
              quantity: quantity,
              key: newKey,
            }
          }
          if (process.env.NODE_ENV !== "production") {
            console.debug(
              `[Cart:addMultiple]`,
              JSON.parse(JSON.stringify(i)),
              JSON.parse(JSON.stringify(draft))
            )
          }
        })
      )
    },
    [setItems]
  )
  const addSingle = useCallback(toAdd => addMultiple(toAdd, 1), [addMultiple])

  const addCollection = useCallback(
    products => {
      return setItems(i =>
        produce(i, draft => {
          const inCartIds = Object.values(i).map(it => it.variantID)
          const [inCartProds, notInCartProds] = products.reduce(
            (acc, curr) => {
              const ix = inCartIds.includes(curr.variantID) ? 0 : 1
              acc[ix].push(curr)
              return acc
            },
            [[], []]
          )
          inCartProds.forEach(product => {
            const inCart = Object.values(i).find(
              ({ variantID, subscription }) => variantID === product.variantID && !subscription
            )
            draft[inCart.key].quantity += product.quantity
          })
          notInCartProds.forEach(toAdd => {
            const newKey = getUUID()
            draft[newKey] = {
              productID: toAdd.productID,
              variantID: toAdd.variantID,
              subscription: false,
              subscriptionID: "",
              quantity: toAdd.quantity,
              key: newKey,
            }
          })
          if (process.env.NODE_ENV !== "production") {
            console.debug(
              `[Cart:addCollection]`,
              JSON.parse(JSON.stringify(i)),
              JSON.parse(JSON.stringify(draft))
            )
          }
        })
      )
    },
    [setItems]
  )

  const decrementQuantity = useCallback(
    key =>
      setItems(i =>
        produce(i, draft => {
          if (draft[key].quantity > 1) {
            draft[key].quantity -= 1
          } else if (draft[key].quantity === 1) {
            delete draft[key]
          }
          if (process.env.NODE_ENV !== "production") {
            console.debug(
              `[Cart:decrementQuantity]`,
              JSON.parse(JSON.stringify(i)),
              JSON.parse(JSON.stringify(draft))
            )
          }
        })
      ),
    [setItems]
  )
  const incrementQuantity = useCallback(
    key =>
      setItems(i =>
        produce(i, draft => {
          if (draft[key].quantity < 10) {
            draft[key].quantity += 1
          }
          if (process.env.NODE_ENV !== "production") {
            console.debug(
              `[Cart:incrementQuantity]`,
              JSON.parse(JSON.stringify(i)),
              JSON.parse(JSON.stringify(draft))
            )
          }
        })
      ),
    [setItems]
  )

  const changeQuantity = useCallback(
    (key, newQuantity) =>
      setItems(i =>
        produce(i, draft => {
          if (newQuantity >= 1 && newQuantity <= 10) {
            draft[key].quantity = newQuantity
          }
          if (process.env.NODE_ENV !== "production") {
            console.debug(
              `[Cart:incrementQuantity]`,
              JSON.parse(JSON.stringify(i)),
              JSON.parse(JSON.stringify(draft))
            )
          }
        })
      ),
    [setItems]
  )

  const changeVariant = useCallback(
    (key, variantID) =>
      setItems(i =>
        produce(i, draft => {
          draft[key].variantID = variantID
          draft[key].subscription = false
          if (process.env.NODE_ENV !== "production") {
            console.debug(
              `[Cart:changeVariant]`,
              JSON.parse(JSON.stringify(i)),
              JSON.parse(JSON.stringify(draft))
            )
          }
        })
      ),
    [setItems]
  )
  const changeSubscription = useCallback(
    (key, subscriptionIDOrNull) =>
      setItems(i =>
        produce(i, draft => {
          if (subscriptionIDOrNull) {
            draft[key].subscription = true
            draft[key].subscriptionID = subscriptionIDOrNull
          } else {
            draft[key].subscription = false
          }
          if (process.env.NODE_ENV !== "production") {
            console.debug(
              `[Cart:changeSubscription]`,
              JSON.parse(JSON.stringify(i)),
              JSON.parse(JSON.stringify(draft))
            )
          }
        })
      ),
    [setItems]
  )
  const remove = useCallback(
    key =>
      setItems(i =>
        produce(i, draft => {
          delete draft[key]
          if (process.env.NODE_ENV !== "production") {
            console.debug(
              `[Cart:remove]`,
              JSON.parse(JSON.stringify(i)),
              JSON.parse(JSON.stringify(draft))
            )
          }
        })
      ),
    [setItems]
  )

  const tryCoupon = useCallback(
    toTry => {
      if (!toTry) {
        setCoupon(null)
        setCouponError("")
        return Promise.resolve(true)
      }
      return fetch("/.netlify/functions/coupon", {
        method: "POST",
        body: JSON.stringify({ code: toTry }),
      })
        .then(r => r.json())
        .then(result => {
          if (result.invalid) {
            setCoupon(null)
            setCouponError(result.invalid)
            return false
          } else if (result.coupon) {
            setCoupon(result.coupon)
            setCouponError("")
            return true
          } else {
            setCoupon(null)
            setCouponError("CANNOT_CHECK")
            return false
          }
        })
        .catch(e => {
          setCoupon(null)
          setCouponError("CANNOT_CHECK")
          return false
        })
    },
    /*  eslint-disable-next-line react-hooks/exhaustive-deps */
    []
  )

  return (
    <CartContext.Provider
      value={{
        lineItems,
        numberOfItems,
        expanded,
        toggle,
        show,
        hide,
        addSingle,
        addMultiple,
        addCollection,
        incrementQuantity,
        decrementQuantity,
        changeQuantity,
        changeSubscription,
        changeVariant,
        remove,
        prices,
        checkoutURL,
        coupon,
        couponError,
        tryCoupon,
      }}
    >
      {children}
    </CartContext.Provider>
  )
}

export { useCart, CartProvider }
